/**
 * The sidebar, and who sees each item. Filtered by ROLE (CLAUDE.md "Roles"):
 * trainers never see Payments, Announcements, Trainers, Import or Settings.
 *
 * This is presentation. What a person can read inside a screen is decided by RLS, and
 * each area's routes.tsx guards its own routes with <RequireRole> as well, so typing
 * /payments as a trainer shows Not found.
 *
 * Adding a screen INSIDE an area needs no change here. Adding a new top-level area:
 * add its item here and its routes.tsx import in router.tsx (both foundation-owned).
 */
import { ADMIN_ROLES, STAFF_ROLES, type StaffRole } from '@/auth/roles';

export type NavIcon =
  | 'dashboard'
  | 'clients'
  | 'messages'
  | 'workouts'
  | 'diet'
  | 'videos'
  | 'payments'
  | 'announcements'
  | 'trainers'
  | 'import'
  | 'settings';

export interface NavItem {
  key: string;
  label: string;
  to: string;
  icon: NavIcon;
  roles: readonly StaffRole[];
  /** What a trainer sees instead (DESIGN.md §4.1: "Today", "My clients"). */
  trainerLabel?: string;
}

export interface NavSection {
  key: string;
  /** Omitted for the first group, which needs no heading. */
  label?: string;
  items: NavItem[];
}

export const NAVIGATION: NavSection[] = [
  {
    key: 'today',
    items: [
      { key: 'dashboard', label: 'Dashboard', trainerLabel: 'Today', to: '/', icon: 'dashboard', roles: STAFF_ROLES },
      { key: 'clients', label: 'Clients', trainerLabel: 'My clients', to: '/clients', icon: 'clients', roles: STAFF_ROLES },
      { key: 'messages', label: 'Messages', to: '/messages', icon: 'messages', roles: STAFF_ROLES },
    ],
  },
  {
    key: 'coaching',
    label: 'Coaching',
    items: [
      { key: 'plans', label: 'Workout plans', to: '/plans', icon: 'workouts', roles: STAFF_ROLES },
      { key: 'diet', label: 'Diet plans', to: '/diet', icon: 'diet', roles: STAFF_ROLES },
      { key: 'videos', label: 'Video library', to: '/videos', icon: 'videos', roles: STAFF_ROLES },
    ],
  },
  {
    key: 'gym',
    label: 'Gym',
    items: [
      { key: 'payments', label: 'Payments', to: '/payments', icon: 'payments', roles: ADMIN_ROLES },
      { key: 'announcements', label: 'Announcements', to: '/announcements', icon: 'announcements', roles: ADMIN_ROLES },
      { key: 'trainers', label: 'Trainers', to: '/trainers', icon: 'trainers', roles: ADMIN_ROLES },
      { key: 'import', label: 'Import members', to: '/import', icon: 'import', roles: ADMIN_ROLES },
      { key: 'settings', label: 'Settings', to: '/settings', icon: 'settings', roles: ADMIN_ROLES },
    ],
  },
];

/** Drop items the role may not see, then sections left empty. */
export function visibleNavigation(role: StaffRole | 'client' | null | undefined): NavSection[] {
  if (!role || role === 'client') return [];
  return NAVIGATION.map((section) => ({
    ...section,
    items: section.items
      .filter((item) => item.roles.includes(role))
      .map((item) => (role === 'trainer' && item.trainerLabel ? { ...item, label: item.trainerLabel } : item)),
  })).filter((section) => section.items.length > 0);
}
